import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

/**
 * IActionEdit
 */
export interface IActionEdit {
	/** ActionEditID */
	ActionEditID: number;
	/** ActionID */
	ActionID: number;
	/** PreviousAction */
	PreviousAction: string;
	/** NewAction */
	NewAction: string;
	/** UserID */
	UserID: string;
	/** TimelineID */
	TimelineID: number;
}

/**
 * ActionEdit
 */
export class ActionEdit {

	/**
	 * Default Constructor
	 * @param ActionEditID ActionEditID 
	 * @param ActionID ActionID 
	 * @param PreviousAction PreviousAction 
	 * @param NewAction NewAction 
	 * @param UserID UserID 
	 * @param TimelineID TimelineID 
	 */
	constructor(
		public ActionEditID: number,
		public ActionID: number,
		public PreviousAction: string,
		public NewAction: string,
		public UserID: string,
		public TimelineID: number
	) { }
}

/** IActionType */
export interface IActionType {
	ActionTypeID: number;
	Description: string;
}

/** IAction */
export interface IAction {
	/** ActionID */
	ActionID: number;

	/** ActionType */
	ActionType: number;

	/** AssetID */
	AssetID: number;

	/** AssetName */
	AssetName: string;

	/** EventID */
	EventID: number;

	/** TimelineID */
	TimeLineID: number;

	/** Timestamp */
	Timestamp: number;

	/** Completed */
	Completed: boolean;

	/** Details */
	Details: string;

	/** CompletedTimestamp */
	CompletedTimestamp: number;

	/** CompletedByUserID  */
	CompletedByUserID: string;

	/** CreatedByUserSub  */
	CreatedByUserSub: string;

	/** AssignedToUserSub */
	AssignedToUserSub?: string;

	/** WorkOrderReference */
	WorkOrderReference?: string;

	/** TargetCompletionTimestamp */
	TargetCompletionTimestamp?: number;

	/** AssignedToUserName */
	AssignedToUserName?: string;
}

/** Action */
export class Action implements IAction {

	/**
	 * Default Constructor
	 * @param ActionID ActionID 
	 * @param ActionType ActionType 
	 * @param AssetID AssetID 
	 * @param AssetName AssetName 
	 * @param EventID EventID 
	 * @param TimeLineID TimeLineID 
	 * @param Timestamp Timestamp 
	 * @param Completed Completed 
	 * @param Details Details 
	 * @param CompletedTimestamp CompletedTimestamp  
	 * @param CompletedByUserID CompletedByUserID 
	 * @param CreatedByUserSub CreatedByUserSub 
	 * @param AssignedToUserSub AssignedToUserSub 
	 * @param WorkOrderReference WorkOrderReference  
	 * @param TargetCompletionTimestamp TargetCompletionTimestamp 
	 * @param AssignedToUserName AssignedToUserName 
	 */
	constructor(
		public ActionID: number,
		public ActionType: number,
		public AssetID: number,
		public AssetName: string,
		public EventID: number,
		public TimeLineID: number,
		public Timestamp: number,
		public Completed: boolean,
		public Details: string,
		public CompletedTimestamp: number,
		public CompletedByUserID: string,
		public CreatedByUserSub: string,
		public AssignedToUserSub?: string,
		public WorkOrderReference?: string,
		public TargetCompletionTimestamp?: number,
		public DisabledRules?: string,
		public AssignedToUserName?: string
	) { }
}

/**
 * Injectable
 */
@Injectable({
	providedIn: 'root'
})
export class ActionsDataService {

	/** apiUnits */
	private apiActions = "api/{0}/organisation/{1}/Actions";

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions Global Functions
	 * @param siteSubscriptions site Subscriptions
	 */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions,
		public siteSubscriptions: siteSubscriptions
	) { }

	/**
	 * Post an action
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param action action 
	 * @param disableRule disableRule 
	 * @returns 
	 */
	postAction$(AccountGUID: string, orgUID: string, action: Action, disableRule?: boolean): Observable<any> {
		let data = JSON.stringify(action);

		let url = this.apiActions;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}`;

		if (disableRule) {
			url += `?disableRule=${disableRule}`;
		}

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Getting action Types
	 * @returns 
	 */
	getActionTypes$(): Observable<any> {

		let url = this.apiActions;
		url = url.replace("{0}", '-');
		url = url.replace("{1}", '-');

		url = `${environment.apiUrl}/${url}/ActionTypes`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", {});
	}

	/**
	 * Getting actions
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param actionID actionID 
	 * @returns 
	 */
	getActions$(AccountGUID: string, orgUID: string, actionID?: number, assetID?: number): Observable<any> {
		let data = {};

		let url = this.apiActions;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}`;

		if (actionID) {
			url += `?actionID=${actionID}`;
		} else if (assetID) {
			url += `?assetID=${assetID}`;
		}
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Complete an action
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param actionID actionID 
	 * @param enableRule enableRule  
	 * @returns 
	 */
	completeAction$(AccountGUID: string, orgUID: string, actionID: number, userID: number, enableRule?: boolean): Observable<any> {
		let data = {};

		let url = this.apiActions;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/complete/${actionID}`;

		url += `?userID=${userID}`;

		if (enableRule) {
			url += `&enableRule=${enableRule}`;
		}

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}


	/**
	 * Patch an action 
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param actionID actionID 
	 * @param action action 
	 * @returns 
	 */
	patchAction$(AccountGUID: string, orgUID: string, actionID: number, action: Action): Observable<any> {
		let data = JSON.stringify(action);

		let url = this.apiActions;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${actionID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * getActionEdits
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param actionID actionID 
	 * @returns 
	 */
	getActionEdits$(AccountGUID: string, orgUID: string, actionID: number): Observable<any> {
		let data = {};

		let url = this.apiActions;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/edits/${actionID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}
}
